import { EarliPointParsedResult } from "@api/assessments/assessment-schema-types";
import type {
  AssessmentLayoutConfig,
  FieldValidation,
  LayoutSection,
  SingleSelectOption,
} from "@components/AssessmentFormRenderer/types/layoutTypes";

// Canonical values. Must match the allowed sets in frontera/api-django/frontera/
// api/models/assessment_schemas/EARLIPOINT/parsed_result.py
// (ALLOWED_DIAGNOSTIC_CLASSIFICATIONS / ALLOWED_SOCIAL_DISABILITY_SEVERITIES) —
// extraction normalizes onto these regardless of report template revision.
const EARLIPOINT_CLASSIFICATION_OPTIONS: SingleSelectOption[] = [
  { label: "Consistent with Autism", value: "Consistent with Autism" },
  { label: "Not Consistent with Autism", value: "Not Consistent with Autism" },
  { label: "Borderline", value: "Borderline" },
];

const EARLIPOINT_SEVERITY_OPTIONS: SingleSelectOption[] = [
  { label: "High Concern", value: "High Concern" },
  { label: "Moderate Concern", value: "Moderate Concern" },
  { label: "Low Concern", value: "Low Concern" },
  { label: "Minimal Concern", value: "Minimal Concern" },
];

const EARLIPOINT_ADOS2_SYMPTOM_LEVEL_OPTIONS: SingleSelectOption[] = [
  { label: "High", value: "High" },
  { label: "Moderate", value: "Moderate" },
  { label: "Low", value: "Low" },
  { label: "Minimal-to-No Evidence", value: "Minimal-to-No Evidence" },
];

// "Not specified in report" is its own selectable option, mapping explicitly
// to null — the Select has no way to clear back to the placeholder once
// Yes/No has been picked, so null needs a real option a reviewer can choose.
const EARLIPOINT_ADDITIONAL_ASSESSMENT_RECOMMENDED_OPTIONS: SingleSelectOption[] = [
  { label: "Yes", value: true },
  { label: "No", value: false },
  { label: "Not specified in report", value: null },
];

// Every EarliPoint field uses its own key as its display label, so a small
// helper collapses the repetitive { type, key, label, editable } shape.
const textField = (key: string, validation?: FieldValidation): LayoutSection => ({
  type: "text_field",
  key,
  label: key,
  editable: true,
  ...(validation && { validation }),
});

const singleSelect = (key: string, options: SingleSelectOption[], placeholder: string): LayoutSection => ({
  type: "single_select",
  key,
  label: key,
  editable: true,
  placeholder,
  options,
});

const NUMBER: FieldValidation = { inputType: "number" };

export const EARLIPOINT_LAYOUT: AssessmentLayoutConfig = {
  parsedResultSchema: EarliPointParsedResult,
  sections: [
    textField("Patient Age at Evaluation"),
    textField("Session Quality"),
    textField("Session Date"),
    textField("Prescribing Clinician"),
    textField("Device Operator"),
    textField("Diagnostic Index Score", NUMBER),
    singleSelect("Diagnostic Classification", EARLIPOINT_CLASSIFICATION_OPTIONS, "Select classification..."),
    textField("Diagnostic Narrative"),
    singleSelect(
      "Additional Assessment Recommended",
      EARLIPOINT_ADDITIONAL_ASSESSMENT_RECOMMENDED_OPTIONS,
      "Not specified in report",
    ),
    textField("False Negative Statement"),
    textField("Social Disability Index Score", NUMBER),
    singleSelect("Social Disability Severity", EARLIPOINT_SEVERITY_OPTIONS, "Select severity..."),
    textField("Estimated ADOS-2 Total Score", NUMBER),
    singleSelect("Estimated ADOS-2 Symptom Level", EARLIPOINT_ADOS2_SYMPTOM_LEVEL_OPTIONS, "Select symptom level..."),
    textField("Language Comprehension Index Score", NUMBER),
    textField("Language Comprehension Age Equivalent"),
    textField("Language Comprehension Age Equivalent CI"),
    textField("Language Comprehension Comparison Sentence"),
    textField("Nonverbal Learning Index Score", NUMBER),
    textField("Nonverbal Learning Age Equivalent"),
    textField("Nonverbal Learning Age Equivalent CI"),
    textField("Nonverbal Learning Comparison Sentence"),
  ],
};
